import React from 'react';
import './ErrorBoundary.css';
import { reportClientError } from '../../services/errorReporter';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error to console and any error reporting service
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    reportClientError({
      message: error.message || 'React error boundary caught an error',
      error,
      severity: 'fatal',
      category: this.props.errorCategory || 'react_error_boundary',
      metadata: {
        componentStack: errorInfo?.componentStack || null,
        title: this.props.title || null,
      },
    });
    
    this.setState({
      error: error,
      errorInfo: errorInfo
    });

    // Call optional error callback
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    if (this.props.onRetry) {
      this.props.onRetry();
    }
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <div className="error-boundary">
          <div className="error-boundary__content">
            <h2 className="error-boundary__title">
              {this.props.title || 'Something went wrong'}
            </h2>
            <p className="error-boundary__message">
              {this.props.message || 'An unexpected error occurred. Please try again.'}
            </p>
            
            {this.props.showRetry !== false && (
              <button 
                className="error-boundary__retry-btn"
                onClick={this.handleRetry}
              >
                Try Again
              </button>
            )}

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="error-boundary__details">
                <summary>Error Details (Development Only)</summary>
                <pre className="error-boundary__stack">
                  {this.state.error.toString()}
                  {this.state.errorInfo.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
